/**
 * CEO Command Center - AI Chat Sidebar
 * 
 * AI-powered chat sidebar for natural language interactions:
 * - Chat with AI assistant about tasks, deals, priorities
 * - RAG-enhanced responses with knowledge base context
 * - Execute commands through natural language
 * - Quick action suggestions
 * 
 * @module components/features/chat
 * @version 10.2.0
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../../utils/format';
import { Button, Spinner, Badge } from '../../ui';
import { openaiService, DEFAULT_SYSTEM_PROMPT, ChatMessage, AICommand } from '../../../services/api/openai';
import { chatStorage } from '../../../services/storage';
import { useKnowledgeStore, useDataStoreSQLite } from '../../../stores';
import { getOpenAITools, getToolByName } from '../../../services/ai/tools';
import { executeAITool, toolRequiresConfirmation, createPendingAction, type PendingAction, type DataStore } from '../../../services/ai/executor';
import type { Task, Deal, Signal } from '../../../types';

// TYPES

interface RAGSource {
  id: string;
  title: string;
  category: string;
  relevance: number;
}

interface ChatMessageWithSources extends ChatMessage {
  sources?: RAGSource[];
}

interface ChatSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onCommand?: (command: AICommand) => Promise<void> | void;
  context?: {
    tasks: Task[];
    deals: Deal[];
    signals: Signal[];
  };
  className?: string;
  enableRAG?: boolean;
  enableTools?: boolean;
}

// TOOL CONFIRMATION COMPONENT

interface ToolConfirmationProps {
  pendingAction: PendingAction;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ToolConfirmation: React.FC<ToolConfirmationProps> = ({
  pendingAction,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const tool = getToolByName(pendingAction.toolName);

  return (
    <div className="mx-4 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-start gap-3">
        <span className="text-xl">⚠️</span>
        <div className="flex-1">
          <p className="font-medium text-amber-900 mb-1">Confirm Action</p>
          <p className="text-sm text-amber-800 mb-3">
            {pendingAction.confirmationMessage}
          </p>
          {tool && (
            <p className="text-xs text-amber-600 mb-3">
              Category: {tool.category}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? <Spinner size="sm" /> : 'Confirm'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MessageBubbleProps {
  message: ChatMessageWithSources;
  isUser: boolean;
}

// MESSAGE BUBBLE

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isUser }) => {
  const [showSources, setShowSources] = useState(false);
  
  return (
    <div className={cn(
      'flex flex-col',
      isUser ? 'items-end' : 'items-start'
    )}>
      <div className={cn(
        'max-w-[85%] rounded-2xl px-4 py-2',
        isUser
          ? 'bg-primary-500 text-white rounded-br-md'
          : 'bg-gray-100 text-gray-900 rounded-bl-md'
      )}>
        <div className="text-sm whitespace-pre-wrap break-words">
          {message.content}
        </div>
      </div>
      
      {/* Knowledge Sources */}
      {message.sources && message.sources.length > 0 && (
        <div className="mt-1 max-w-[85%]">
          <button
            onClick={() => setShowSources(!showSources)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            📚 {message.sources.length} source{message.sources.length > 1 ? 's' : ''} used
            <span className="text-[10px]">{showSources ? '▲' : '▼'}</span>
          </button>
          
          {showSources && (
            <div className="mt-1 p-2 bg-blue-50 rounded-lg text-xs space-y-1">
              {message.sources.map(source => (
                <div key={source.id} className="flex items-center justify-between">
                  <span className="text-gray-700">{source.title}</span>
                  <Badge className="text-[10px] bg-blue-100 text-blue-700">
                    {source.category}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// QUICK ACTIONS

interface QuickActionsProps {
  onSelect: (prompt: string) => void;
  onFileUpload: (file: File) => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({ onSelect, onFileUpload }) => {
  const actions = [
    { label: '📊 Daily Summary', prompt: 'Give me a quick summary of my priorities today.' },
    { label: '⚠️ Top Signals', prompt: 'What are the most urgent signals I need to address?' },
    { label: '📈 Pipeline Status', prompt: 'How is my sales pipeline looking?' },
    { label: '✅ Blocked Tasks', prompt: 'What tasks are currently blocked?' },
  ];
  
  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.md,.csv,.json,.pdf,.docx';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        onFileUpload(file);
      }
    };
    input.click();
  };
  
  return (
    <div className="p-3 border-t bg-gray-50">
      <p className="text-xs text-gray-500 mb-2">Quick Actions</p>
      <div className="flex flex-wrap gap-2">
        {actions.map(action => (
          <button
            key={action.label}
            onClick={() => onSelect(action.prompt)}
            className="px-3 py-1.5 text-xs bg-white border rounded-full hover:bg-gray-100 transition-colors"
          >
            {action.label}
          </button>
        ))}
        <button
          onClick={handleFileSelect}
          className="px-3 py-1.5 text-xs bg-primary-50 border border-primary-200 text-primary-700 rounded-full hover:bg-primary-100 transition-colors flex items-center gap-1"
          title="Upload file to AI knowledge"
        >
          📎 Add File
        </button>
      </div>
    </div>
  );
};

// CHAT SIDEBAR

/**
 * AI Chat Sidebar component
 * Provides AI-powered assistance through natural language
 * Supports RAG (Retrieval Augmented Generation) with knowledge base
 */
export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  isOpen,
  onToggle,
  onCommand,
  context,
  className,
  enableRAG = true,
  enableTools = true,
}) => {
  const [messages, setMessages] = useState<ChatMessageWithSources[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Knowledge store for RAG indicator and file uploads
  const { items: knowledgeItems, addItem } = useKnowledgeStore();
  const hasKnowledge = knowledgeItems.length > 0;

  // Data store for tool execution
  const dataStore = useDataStoreSQLite();
  
  // Load chat history on mount
  useEffect(() => {
    const history = chatStorage.getHistory();
    if (history.length > 0) {
      setMessages(history as ChatMessageWithSources[]);
    }
  }, []);
  
  // Save messages to storage when they change
  useEffect(() => {
    if (messages.length > 0) {
      // Only save user and assistant messages, not system
      const toSave = messages.filter(m => m.role !== 'system');
      chatStorage.clearHistory();
      toSave.forEach(m => chatStorage.addMessage(m));
    }
  }, [messages]);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Focus input when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  
  // Build system message with context
  const buildSystemMessage = useCallback((): ChatMessage => {
    let contextStr = '';
    
    if (context) {
      contextStr = openaiService.buildContext({
        tasks: context.tasks,
        deals: context.deals,
        signals: context.signals,
      });
    }
    
    const customPrompt = chatStorage.getSystemPrompt();
    const systemPrompt = customPrompt || DEFAULT_SYSTEM_PROMPT;
    
    return {
      role: 'system',
      content: `${systemPrompt}\n\n${contextStr}`,
    };
  }, [context]);
  
  // Build data store interface for executor
  const buildDataStoreInterface = useCallback((): DataStore => {
    return {
      cashflow: dataStore.cashflow,
      updateCashFlowField: dataStore.updateCashFlowField,
      createCashFlowRecord: dataStore.createCashFlowRecord,
      deals: dataStore.deals,
      createDeal: dataStore.createDeal,
      updateDeal: dataStore.updateDeal,
      deleteDeal: dataStore.deleteDeal,
      tasks: dataStore.tasks,
      createTask: dataStore.createTask,
      updateTask: dataStore.updateTask,
      clients: dataStore.clients,
      createClient: dataStore.createClient || (async () => ({})),
      updateClient: dataStore.updateClient || (async () => ({})),
      investors: dataStore.investors,
      addInvestor: dataStore.addInvestor,
      updateInvestor: dataStore.updateInvestor,
    };
  }, [dataStore]);

  // Execute a confirmed tool action
  const executeToolAction = useCallback(async (action: PendingAction) => {
    const store = buildDataStoreInterface();
    const result = await executeAITool(action.toolName, action.params, store);

    // Add result message
    const resultMessage: ChatMessageWithSources = {
      role: 'assistant',
      content: result.success
        ? `✓ ${result.message}`
        : `✗ ${result.message}`,
    };
    setMessages(prev => [...prev, resultMessage]);

    return result;
  }, [buildDataStoreInterface]);

  // Handle tool confirmation
  const handleConfirmAction = useCallback(async () => {
    if (!pendingAction) return;

    setConfirmLoading(true);
    try {
      await executeToolAction(pendingAction);
    } catch (e) {
      console.error('Tool execution error:', e);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✗ Failed to execute action: ${e instanceof Error ? e.message : 'Unknown error'}`,
      }]);
    } finally {
      setConfirmLoading(false);
      setPendingAction(null);
    }
  }, [pendingAction, executeToolAction]);

  // Handle cancel action
  const handleCancelAction = useCallback(() => {
    setPendingAction(null);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Action cancelled.',
    }]);
  }, []);

  // Send message to AI
  const sendMessage = async (content: string) => {
    if (!content.trim() || loading) return;

    setError(null);
    setInput('');
    setPendingAction(null);

    // Add user message
    const userMessage: ChatMessageWithSources = { role: 'user', content };
    setMessages(prev => [...prev, userMessage]);

    setLoading(true);

    try {
      let assistantContent: string;
      let sources: RAGSource[] = [];

      // Build messages
      const systemMessage = buildSystemMessage();
      const recentMessages = messages.slice(-10);
      const allMessages = [
        systemMessage,
        ...recentMessages,
        userMessage,
      ] as ChatMessage[];

      // Use tool-enabled chat if enabled
      if (enableTools) {
        const tools = getOpenAITools();

        // Enhanced system prompt for tool use
        const toolSystemMessage: ChatMessage = {
          role: 'system',
          content: `${systemMessage.content}

You have access to tools that can modify data in the system. When the user asks you to update, create, or modify data (like cash positions, deals, tasks, clients, or investors), use the appropriate tool.

Available tools:
- Finance: update_cash_position, update_cashflow_revenue, update_cashflow_expense, create_cashflow_record
- Sales: create_deal, update_deal, delete_deal
- Tasks: create_task, update_task, complete_task
- Clients: create_client, update_client
- Investors: create_investor, update_investor

Use tools when the user clearly wants to modify data. For queries or analysis, respond with text.`,
        };

        const toolMessages = [
          toolSystemMessage,
          ...recentMessages,
          userMessage,
        ] as ChatMessage[];

        const response = await openaiService.chatWithTools(toolMessages, tools);

        if (response.toolCalls && response.toolCalls.length > 0) {
          // AI wants to use tools
          const toolResults: Array<{ role: 'tool'; tool_call_id: string; content: string }> = [];

          for (const toolCall of response.toolCalls) {
            const toolName = toolCall.function.name;
            const params = JSON.parse(toolCall.function.arguments);

            // Check if confirmation is required
            if (toolRequiresConfirmation(toolName)) {
              // Set pending action for confirmation
              const pending = createPendingAction(toolName, params);
              setPendingAction(pending);

              // Add pending message
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `I can ${pending.confirmationMessage.replace('?', '.')} Please confirm this action.`,
              }]);

              setLoading(false);
              return; // Wait for confirmation
            }

            // Execute tool without confirmation
            const store = buildDataStoreInterface();
            const result = await executeAITool(toolName, params, store);

            toolResults.push({
              role: 'tool',
              tool_call_id: toolCall.id,
              content: JSON.stringify(result),
            });
          }

          // Continue conversation with tool results
          if (toolResults.length > 0) {
            const continueMessages = [
              ...toolMessages,
              {
                role: 'assistant' as const,
                content: response.content || '',
                tool_calls: response.toolCalls,
              },
              ...toolResults,
            ];

            assistantContent = await openaiService.continueAfterToolCall(
              continueMessages as Array<ChatMessage | { role: 'tool'; tool_call_id: string; content: string }>
            );
          } else {
            assistantContent = response.content || 'Action completed.';
          }
        } else {
          // No tool calls, use the text response
          assistantContent = response.content || 'Sorry, I could not process that request.';
        }
      } else if (enableRAG && hasKnowledge) {
        // Use RAG-enhanced chat
        const recentMsgs = messages.slice(-10) as ChatMessage[];
        const response = await openaiService.chatWithRAG(content, recentMsgs);
        assistantContent = response.content;
        sources = response.sources;
      } else {
        // Use standard chat
        const response = await openaiService.chat(allMessages);
        assistantContent = response.choices[0]?.message?.content || 'Sorry, I could not process that request.';
      }

      // Add assistant message with sources
      const assistantMessage: ChatMessageWithSources = {
        role: 'assistant',
        content: assistantContent,
        sources: sources.length > 0 ? sources : undefined,
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Parse and execute commands if handler provided (legacy support)
      if (onCommand) {
        const commands = openaiService.parseCommands(assistantContent);
        for (const cmd of commands) {
          try {
            await onCommand(cmd);
          } catch (e) {
            console.error('Failed to execute command:', e);
          }
        }
      }
    } catch (e) {
      console.error('Chat error:', e);
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';

      // Provide specific error messages based on the error type
      let userMsg = 'Sorry, I encountered an error. ';
      if (errorMessage.includes('API') || errorMessage.includes('fetch') || errorMessage.includes('network')) {
        userMsg += 'Unable to reach the AI service. Please check your internet connection and try again.';
        setError('Connection to AI service failed. Check your network connection.');
      } else if (errorMessage.includes('rate') || errorMessage.includes('limit')) {
        userMsg += 'Too many requests. Please wait a moment before trying again.';
        setError('Rate limit reached. Please wait before sending more messages.');
      } else if (errorMessage.includes('key') || errorMessage.includes('auth')) {
        userMsg += 'There seems to be a configuration issue. Please contact support.';
        setError('AI service authentication failed. Check API configuration.');
      } else {
        userMsg += 'Please try again. If the problem persists, try refreshing the page.';
        setError('Failed to get response. Please try again.');
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: userMsg,
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };
  
  // Handle key press (Enter to send, Shift+Enter for newline)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };
  
  // Clear chat history
  const clearChat = () => {
    setMessages([]);
    chatStorage.clearHistory();
  };
  
  // Handle file upload for RAG
  const handleFileUpload = async (file: File) => {
    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    const supportedExts = ['txt', 'md', 'csv', 'json'];
    if (!ext || !supportedExts.includes(ext)) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ File type ".${ext || 'unknown'}" is not supported. Please upload a text-based file (TXT, MD, CSV, JSON).`,
      }]);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ File "${file.name}" is too large. Maximum file size is 5MB.`,
      }]);
      return;
    }

    try {
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (!result.trim()) {
            reject(new Error('File is empty'));
            return;
          }
          resolve(result);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });

      // Add to knowledge store
      addItem({
        title: file.name,
        content: content.substring(0, 10000), // Limit content size
        category: 'general',
        tags: ['uploaded', 'chat', file.name.split('.').pop() || 'file'],
        source: 'Chat Upload',
      });

      // Also save to RAG files storage
      const ragFilesKey = 'sentiance_rag_files';
      const existingFiles = JSON.parse(localStorage.getItem(ragFilesKey) || '[]');
      existingFiles.push({
        id: `rag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type || 'Unknown',
        content: content.substring(0, 10000),
        uploadedAt: new Date().toISOString(),
      });
      localStorage.setItem(ragFilesKey, JSON.stringify(existingFiles));

      // Add confirmation message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ File "${file.name}" has been added to my knowledge base. I can now reference this information when answering your questions.`,
      }]);
    } catch (error) {
      console.error('Failed to upload file:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Failed to process "${file.name}": ${errorMsg}`,
      }]);
    }
  };
  
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className={cn(
          'fixed right-4 bottom-4 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white text-2xl transition-transform hover:scale-110 z-40',
          'bg-gradient-to-br from-coral-500 to-coral-600',
          className
        )}
        title="Open AI Assistant"
      >
        🤖
      </button>
    );
  }
  
  return (
    <div className={cn(
      'fixed right-0 top-0 h-full w-96 bg-white shadow-2xl flex flex-col z-50 border-l',
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="flex items-center gap-2">
          <span className="text-xl">🤖</span>
          <div>
            <h3 className="font-semibold">AI Assistant</h3>
            <div className="flex items-center gap-2 text-xs text-gray-300">
              <span>GPT-4o</span>
              {enableRAG && hasKnowledge && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded">
                  📚 RAG Active
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearChat}
            className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg"
            title="Clear chat"
          >
            🗑️
          </button>
          <button
            onClick={onToggle}
            className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg"
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">👋</div>
            <h4 className="font-medium text-gray-900 mb-1">Hello!</h4>
            <p className="text-sm text-gray-500">
              I'm your AI assistant. Ask me about tasks, deals, or priorities.
            </p>
          </div>
        ) : (
          messages
            .filter(m => m.role !== 'system')
            .map((message, index) => (
              <MessageBubble
                key={index}
                message={message}
                isUser={message.role === 'user'}
              />
            ))
        )}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-sm text-gray-500">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="text-center text-sm text-red-500 py-2">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Tool Confirmation */}
      {pendingAction && (
        <ToolConfirmation
          pendingAction={pendingAction}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
          loading={confirmLoading}
        />
      )}

      {/* Quick Actions */}
      <QuickActions onSelect={sendMessage} onFileUpload={handleFileUpload} />
      
      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={loading}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!input.trim() || loading}
            className="rounded-xl"
          >
            Send
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </form>
    </div>
  );
};

// CHAT TOGGLE BUTTON

interface ChatToggleProps {
  isOpen: boolean;
  onToggle: () => void;
  hasUnread?: boolean;
}

export const ChatToggle: React.FC<ChatToggleProps> = ({
  isOpen,
  onToggle,
  hasUnread = false,
}) => {
  if (isOpen) return null;
  
  return (
    <button
      onClick={onToggle}
      className="fixed right-4 bottom-4 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white text-2xl transition-transform hover:scale-110 z-40 bg-gradient-to-br from-coral-500 to-coral-600"
    >
      🤖
      {hasUnread && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white" />
      )}
    </button>
  );
};

